import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getSemester,
  getSemesters,
  createSemester,
  updateSemester,
  getSemesterMonths,
  createSemesterMonths,
  updateSemesterMonths,
  getSemesterCourses,
  createSemesterCourses,
  updateSemesterCourses,
  createSemesterInfo,
  getCurrentSemester,
} from '../api/semesters.api';
import {
  ISemester,
  IGetSemester,
  IGetSemesters,
  ISemesterMonth,
  IGetSemesterMonths,
  ISemesterCourse,
  IGetSemesterCourses,
  ICurrentSemester,
  IGetCurrentSemester,
} from '../models/semesters.model';

interface ISemesterState {
  semester?: ISemester;
  semesters?: ISemester[];
  months?: ISemesterMonth[];
  semesterCourses?: ISemesterCourse[];
  currentSemester?: ICurrentSemester;
  isLoading?: boolean;
  error?: AxiosError | string;
}

const initialState: ISemesterState = {
  semester: undefined,
  semesters: [],
  months: [],
  semesterCourses: [],
  currentSemester: undefined,
  isLoading: false,
};

const semesterSlicer = createSlice({
  name: 'semester',
  initialState,
  reducers: {
    getSemesterStart: (state: ISemesterState) => {
      state.semester = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getSemesterSuccess: (
      state: ISemesterState,
      action: PayloadAction<IGetSemester>,
    ) => {
      state.semester = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSemesterFailure: (
      state: ISemesterState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.semester = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getSemestersStart: (state: ISemesterState) => {
      state.semesters = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getSemestersSuccess: (
      state: ISemesterState,
      action: PayloadAction<IGetSemesters>,
    ) => {
      state.semesters = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSemestersFailure: (
      state: ISemesterState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.semesters = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getSemesterMonthsStart: (state: ISemesterState) => {
      state.months = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getSemesterMonthsSuccess: (
      state: ISemesterState,
      action: PayloadAction<IGetSemesterMonths>,
    ) => {
      state.months = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSemesterMonthsFailure: (
      state: ISemesterState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.months = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getSemesterCoursesStart: (state: ISemesterState) => {
      state.semesterCourses = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getSemesterCoursesSuccess: (
      state: ISemesterState,
      action: PayloadAction<IGetSemesterCourses>,
    ) => {
      state.semesterCourses = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getSemesterCoursesFailure: (
      state: ISemesterState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.semesterCourses = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getCurrentSemesterStart: (state: ISemesterState) => {
      state.currentSemester = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getCurrentSemesterSuccess: (
      state: ISemesterState,
      action: PayloadAction<IGetCurrentSemester>,
    ) => {
      state.currentSemester = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getCurrentSemesterFailure: (
      state: ISemesterState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.currentSemester = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ISemesterState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: ISemesterState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: ISemesterState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: ISemesterState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getSemesterStart,
  getSemesterSuccess,
  getSemesterFailure,
  getSemestersStart,
  getSemestersSuccess,
  getSemestersFailure,
  getSemesterMonthsStart,
  getSemesterMonthsSuccess,
  getSemesterMonthsFailure,
  getSemesterCoursesStart,
  getSemesterCoursesSuccess,
  getSemesterCoursesFailure,
  getCurrentSemesterStart,
  getCurrentSemesterSuccess,
  getCurrentSemesterFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
} = semesterSlicer.actions;

export default semesterSlicer.reducer;

export const doGetSemester =
  (semesterCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getSemesterStart());
      const response = await getSemester(semesterCode);
      dispatch(getSemesterSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetSemesters = (): any => async (dispatch: any) => {
  try {
    dispatch(getSemestersStart());
    const response = await getSemesters();
    dispatch(getSemestersSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getSemestersFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetCurrentSemester = (): any => async (dispatch: any) => {
  try {
    dispatch(getCurrentSemesterStart());
    const response = await getCurrentSemester();
    dispatch(getCurrentSemesterSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getCurrentSemesterFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateSemester =
  (data: ISemester): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createSemester(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateSemester =
  (id: string, data: ISemester): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateSemester(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetSemesterMonths =
  (semesterCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getSemesterMonthsStart());
      const response = await getSemesterMonths(semesterCode);
      dispatch(getSemesterMonthsSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getSemesterMonthsFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateSemesterMonths =
  (id: string, data: ISemesterMonth[]): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createSemesterMonths(id, data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateSemesterMonths =
  (id: string, data: ISemesterMonth[]): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateSemesterMonths(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetSemesterCourses =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getSemesterCoursesStart());
      const response = await getSemesterCourses(id);
      dispatch(getSemesterCoursesSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getSemesterCoursesFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateSemesterCourses =
  (data: ISemesterCourse[]): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createSemesterCourses(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateSemesterCourses =
  (data: ISemesterCourse[]): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateSemesterCourses(data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateSemesterInfo =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createSemesterInfo(id);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
